import React, { useState, useEffect } from 'react';
import { AdminRequestModalProps } from '../types';
import './AdminRequestModal.css';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import RevisionButton from './RevisionButton';
import SupplierActionButton from './SupplierActionButton';
import EmailModal from './EmailModal';
import PurchaseHistoryModal from './PurchaseHistoryModal';
import SupplierDatabaseModal from './SupplierDatabaseModal';
import ConfirmationModal from './ConfirmationModal';
import EditProductModal from './EditProductModal';
import PreviewModal from './PreviewModal';

// Region options
const REGION_OPTIONS = [
  { value: 'CH', label: 'Switzerland (CH)' },
  { value: 'DE', label: 'Germany (DE)' },
  { value: 'TR', label: 'Turkey (TR)' },
  { value: 'DACH', label: 'DACH Region' },
  { value: 'EU', label: 'European Union (EU)' },
  { value: 'US', label: 'United States (US)' },
  { value: 'ASIA', label: 'Asia Pacific' },
  { value: 'GLOBAL', label: 'Global' }
];

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'TRY', label: 'Turkish Lira (TRY)' }
];

// Unit options
const UNIT_OPTIONS = [
  { value: 'piece', label: 'Piece' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'm', label: 'Meter' },
  { value: 'liter', label: 'Liter' },
  { value: 'box', label: 'Box' },
  { value: 'set', label: 'Set' },
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' }
];

// Predefined filter suggestions
const FILTER_SUGGESTIONS = [
  {
    key: 'Certificate',
    label: 'Certificate Filter',
    type: 'text',
    placeholder: 'Enter certification requirements (e.g., CE, ISO 9001, FDA)'
  },
  {
    key: 'Color',
    label: 'Color Filter',
    type: 'dropdown',
    options: ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'White', 'Gray', 'Orange', 'Purple', 'Pink', 'Brown', 'Custom']
  },
  {
    key: 'Size',
    label: 'Size Filter',
    type: 'dropdown',
    options: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom Size', 'Standard Size']
  },
  {
    key: 'Standard and Norm',
    label: 'Standard and Norm Filter',
    type: 'dropdown',
    options: ['ISO 9001', 'ISO 14001', 'CE Marking', 'FDA Approved', 'RoHS Compliant', 'REACH Compliant', 'UL Listed', 'Custom Standard']
  }
];

const AdminRequestModal: React.FC<AdminRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  onApprove,
  onReject
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshingLocal, setIsRefreshingLocal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [filteredSuppliers, setFilteredSuppliers] = useState<any[]>([]);
  const [filterCriteria, setFilterCriteria] = useState({
    region: '',
    price: '',
    delivery: '',
    emailStatus: 'all' // 'all', 'sent', 'not_sent'
  });
  const [activeTab, setActiveTab] = useState<'ai' | 'local' | 'approved' | 'manual'>('ai');

  // Confirmation modal state
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // Email modal state for local suppliers
  const [localEmailModal, setLocalEmailModal] = useState({
    isOpen: false,
    supplierIndex: -1,
    supplierName: '',
    supplierEmail: ''
  });

  // Purchase History modal state
  const [isPurchaseHistoryOpen, setIsPurchaseHistoryOpen] = useState(false);

  // OCR Database modal state
  const [ocrDatabaseModal, setOcrDatabaseModal] = useState({
    isOpen: false,
    requestData: null as any
  });

  // Approve Request modal state
  const [approveModal, setApproveModal] = useState({
    isOpen: false,
    localSuppliers: [] as any[],
    selectedSuppliers: [] as any[],
    isLoading: false
  });

  // Manual Add form state
  const [manualSupplierForm, setManualSupplierForm] = useState({
    supplier_name: '',
    supplier_email: '',
    supplier_phone: '',
    supplier_website: '',
    supplier_region: 'TR',
    product_name: '',
    product_brand: '',
    product_description: '',
    product_category: '',
    price: '',
    currency: 'TRY',
    delivery_time: '',
    minimum_order: '',
    payment_terms: '',
    notes: ''
  });
  const [manualSuppliers, setManualSuppliers] = useState<any[]>([]);
  const [isAddingManual, setIsAddingManual] = useState(false);

  // Edit Product Modal state
  const [editProductModal, setEditProductModal] = useState({
    isOpen: false,
    selectedSuppliers: [] as any[]
  });

  // Preview Modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    updatedSuppliers: [] as any[]
  });

  const handleOpenApproveModal = async () => {
    if (!request) return;
    
    setApproveModal(prev => ({ ...prev, isOpen: true, isLoading: true }));
    
    try {
              const response = await fetch(API_ENDPOINTS.LOCAL_SUPPLIERS(request._id), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setApproveModal(prev => ({ 
          ...prev, 
          localSuppliers: result.data,
          isLoading: false 
        }));
      } else {
        console.error('Failed to fetch local suppliers:', result.error);
        setApproveModal(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error fetching local suppliers:', error);
      setApproveModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleCloseApproveModal = () => {
    setApproveModal({
      isOpen: false,
      localSuppliers: [],
      selectedSuppliers: [],
      isLoading: false
    });
  };

  const handleSupplierSelection = (supplier: any, isSelected: boolean) => {
    console.log('🔍 SUPPLIER SELECTION DEBUG - supplier:', supplier);
    console.log('🔍 SUPPLIER SELECTION DEBUG - isSelected:', isSelected);
    console.log('🔍 SUPPLIER SELECTION DEBUG - Current selectedSuppliers:', approveModal.selectedSuppliers);
    
    if (isSelected) {
      const newSelectedSuppliers = [...approveModal.selectedSuppliers, supplier];
      console.log('🔍 SUPPLIER SELECTION DEBUG - Adding supplier, new list:', newSelectedSuppliers);
      setApproveModal(prev => ({
        ...prev,
        selectedSuppliers: newSelectedSuppliers
      }));
    } else {
      const newSelectedSuppliers = approveModal.selectedSuppliers.filter(s => 
        s.supplier_id !== supplier.supplier_id || s.product_id !== supplier.product_id
      );
      console.log('🔍 SUPPLIER SELECTION DEBUG - Removing supplier, new list:', newSelectedSuppliers);
      setApproveModal(prev => ({
        ...prev,
        selectedSuppliers: newSelectedSuppliers
      }));
    }
  };

  const handleApproveWithSuppliers = async () => {
    if (!request) return;
    
    console.log('🔍 APPROVE DEBUG - approveModal.selectedSuppliers:', approveModal.selectedSuppliers);
    console.log('🔍 APPROVE DEBUG - approveModal.selectedSuppliers.length:', approveModal.selectedSuppliers.length);
    console.log('🔍 APPROVE DEBUG - approveModal.selectedSuppliers type:', typeof approveModal.selectedSuppliers);
    
    if (approveModal.selectedSuppliers.length === 0) {
      alert(t('components.admin_request_modal.please_select_suppliers'));
      return;
    }

    // Open Edit Product Modal instead of directly approving
    setEditProductModal({
      isOpen: true,
      selectedSuppliers: approveModal.selectedSuppliers
    });
    
    // Close the approve modal
    handleCloseApproveModal();
  };

  const handleApproveRequest = async (updatedSuppliers: any[]) => {
    if (!request) return;

    try {
      const requestBody = {
        selected_suppliers: updatedSuppliers
      };
      
      console.log('🔍 APPROVE DEBUG - Request body:', requestBody);
      console.log('🔍 APPROVE DEBUG - Request body JSON:', JSON.stringify(requestBody));
      
      const response = await fetch(API_ENDPOINTS.APPROVE_REQUEST(request._id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔍 APPROVE DEBUG - Response status:', response.status);
      console.log('🔍 APPROVE DEBUG - Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔍 APPROVE DEBUG - Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 APPROVE DEBUG - Response result:', result);
      
      if (result.success) {
        alert(t('common.request_approved_success', { count: result.selected_suppliers_count }));
        // Close all modals
        setPreviewModal({ isOpen: false, updatedSuppliers: [] });
        setEditProductModal({ isOpen: false, selectedSuppliers: [] });
        handleCloseWithoutConfirmation(); // Close the main modal without confirmation
      } else {
        alert(t('common.failed_to_approve', { message: result.message }));
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert(t('common.error_approving'));
    }
  };

  // Edit Product Modal handlers
  const handleSaveChanges = (updatedSuppliers: any[]) => {
    console.log('🔍 SAVE CHANGES - Updated suppliers:', updatedSuppliers);
    // Update the edit modal state with the new data
    setEditProductModal(prev => ({
      ...prev,
      selectedSuppliers: updatedSuppliers
    }));
  };

  const handleContinueToPreview = (updatedSuppliers: any[]) => {
    console.log('🔍 DEBUG - AdminRequestModal - handleContinueToPreview called with:', updatedSuppliers);
    console.log('🔍 DEBUG - AdminRequestModal - updatedSuppliers.length:', updatedSuppliers?.length);
    console.log('🔍 DEBUG - AdminRequestModal - First supplier structure:', updatedSuppliers?.[0]);
    console.log('🔍 DEBUG - AdminRequestModal - First supplier attached_documents:', updatedSuppliers?.[0]?.attached_documents);
    
    // Debug all suppliers for attached_documents
    updatedSuppliers?.forEach((supplier, index) => {
      console.log(`🔍 DEBUG - AdminRequestModal - Supplier ${index} (${supplier.company_name}) attached_documents:`, supplier.attached_documents);
    });
    
    // Close edit modal and open preview modal
    setEditProductModal(prev => ({ ...prev, isOpen: false }));
    setPreviewModal({
      isOpen: true,
      updatedSuppliers: updatedSuppliers
    });
    
    console.log('🔍 DEBUG - AdminRequestModal - Preview modal state set to:', { isOpen: true, suppliersCount: updatedSuppliers?.length });
  };

  const handleBackToEdit = () => {
    // Close preview modal and open edit modal again
    setPreviewModal(prev => ({ ...prev, isOpen: false }));
    setEditProductModal({
      isOpen: true,
      selectedSuppliers: previewModal.updatedSuppliers
    });
  };

  const handleOpenPurchaseHistory = () => {
    setIsPurchaseHistoryOpen(true);
  };

  const handleClosePurchaseHistory = () => {
    setIsPurchaseHistoryOpen(false);
  };

  // OCR-based supplier/product addition modals
  const [ocrSupplierModal, setOcrSupplierModal] = useState({
    isOpen: false,
    supplierData: null as any
  });

  const [ocrProductModal, setOcrProductModal] = useState({
    isOpen: false,
    productData: null as any
  });

  // Editable product fields
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  const [editableProduct, setEditableProduct] = useState({
    product_name: request?.product_name || '',
    product_brand: request?.product_brand || '',
    product_global_code: request?.product_global_code || '',
    product_company_code: request?.product_company_code || '',
    product_description: request?.product_description || '',
    quantity: request?.quantity || 0,
    unit: request?.unit || '',
    budget: request?.budget || 0,
    currency: request?.currency || 'CHF',
    delivery_time: request?.delivery_time || '',
    importance: request?.importance || '',
    address: request?.address || '',
    region: request?.region || [],
    additional_specifications: request?.additional_specifications || [],
    custom_specifications: request?.custom_specifications || {},
    additional_notes: request?.additional_notes || ''
  });

  // Custom specifications state
  const [newCustomSpecKey, setNewCustomSpecKey] = useState('');
  const [newCustomSpecValue, setNewCustomSpecValue] = useState('');
  const [activeFilterType, setActiveFilterType] = useState<'text' | 'dropdown'>('text');

  // Update editable fields when request changes
  useEffect(() => {
    if (request) {
      setEditableProduct({
        product_name: request.product_name || '',
        product_brand: request.product_brand || '',
        product_global_code: request.product_global_code || '',
        product_company_code: request.product_company_code || '',
        product_description: request.product_description || '',
        quantity: request.quantity || 0,
        unit: request.unit || '',
        budget: request.budget || 0,
        currency: request.currency || 'CHF',
        delivery_time: request.delivery_time || '',
        importance: request.importance || '',
        address: request.address || '',
        region: request.region || [],
        additional_specifications: request.additional_specifications || [],
        custom_specifications: request.custom_specifications || {},
        additional_notes: request.additional_notes || ''
      });
    }
  }, [request]);

  // Load saved analysis results when modal opens
  useEffect(() => {
    if (isOpen && request && request.analysis_result) {
      console.log('Loading saved analysis results:', request.analysis_result);
      
      // Handle both old and new formats
      const result = request.analysis_result;
      
      // Check if it's the new format (has ai_suppliers and local_suppliers)
      if (result.ai_suppliers !== undefined || result.local_suppliers !== undefined) {
        // New format
        setAnalysisResult(result);
        setFilteredSuppliers(result.ai_suppliers || []);
      } else {
        // Old format (has suppliers array)
        setAnalysisResult({
          ai_suppliers: result.suppliers || [],
          local_suppliers: [],
          request_data: result.request_data || {}
        });
        setFilteredSuppliers(result.suppliers || []);
      }
    }
    
    // Load manual suppliers
    if (isOpen && request && request.manual_suppliers) {
      setManualSuppliers(request.manual_suppliers);
    }
  }, [isOpen, request]);

  // Real-time update for email received status
  useEffect(() => {
    if (!isOpen || !request || !request._id) return;

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${request._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.analysis_result) {
            // Update analysis result with latest email received status
            setAnalysisResult(result.data.analysis_result);
          }
        }
      } catch (error) {
        console.error('Error fetching real-time updates:', error);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [isOpen, request?._id, token]);

  if (!isOpen || !request) return null;

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onApprove(request._id);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert(t('common.please_provide_rejection_reason'));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onReject(request._id, rejectionReason);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    console.log('🔍 AdminRequestModal - handleClose called');
    
    // Show confirmation modal instead of window.confirm
    setShowCloseConfirmation(true);
  };

  const handleConfirmClose = () => {
    console.log('🔍 AdminRequestModal - User confirmed closing');
    
    // Only clear essential state, don't clear everything
    setRejectionReason('');
    setAnalysisResult(null);
    setFilteredSuppliers([]);
    setActiveTab('ai');
    
    // Close the modal
    onClose();
  };

  const handleCloseWithoutConfirmation = () => {
    console.log('🔍 AdminRequestModal - handleCloseWithoutConfirmation called');
    
    // Only clear essential state, don't clear everything
    setRejectionReason('');
    setAnalysisResult(null);
    setFilteredSuppliers([]);
    setActiveTab('ai');
    
    // Close the modal without confirmation
    onClose();
  };

  const handleRevisionRequested = () => {
    // Refresh the parent component to show updated status
    window.location.reload();
  };

  const handleActionCompleted = async (supplierIndex?: number) => {
    // Update the local state directly since the backend update is already done
    if (analysisResult && supplierIndex !== undefined) {
      const updatedAnalysisResult = { ...analysisResult };
      
      // Update ai_suppliers if they exist
      if (updatedAnalysisResult.ai_suppliers && supplierIndex < updatedAnalysisResult.ai_suppliers.length) {
        updatedAnalysisResult.ai_suppliers[supplierIndex] = {
          ...updatedAnalysisResult.ai_suppliers[supplierIndex],
          added_to_db: true,
          added_to_db_at: new Date().toISOString()
        };
      }
      
      // Update local_suppliers if they exist
      if (updatedAnalysisResult.local_suppliers) {
        const localIndex = supplierIndex - (updatedAnalysisResult.ai_suppliers?.length || 0);
        if (localIndex >= 0 && localIndex < updatedAnalysisResult.local_suppliers.length) {
          updatedAnalysisResult.local_suppliers[localIndex] = {
            ...updatedAnalysisResult.local_suppliers[localIndex],
            added_to_db: true,
            added_to_db_at: new Date().toISOString()
          };
        }
      }
      
      setAnalysisResult(updatedAnalysisResult);
      setFilteredSuppliers(updatedAnalysisResult.ai_suppliers || []);
      console.log('✅ Supplier marked as added to DB in local state');
    }
    
    // Also fetch fresh data to ensure we have the latest state
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUEST_DETAIL(request._id)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Handle both old and new formats
          const analysisResult = result.data.analysis_result;
          
          if (analysisResult) {
            // Check if it's the new format
            if (analysisResult.ai_suppliers !== undefined || analysisResult.local_suppliers !== undefined) {
              // New format
              setAnalysisResult(analysisResult);
              setFilteredSuppliers(analysisResult.ai_suppliers || []);
            } else {
              // Old format
              setAnalysisResult({
                ai_suppliers: analysisResult.suppliers || [],
                local_suppliers: [],
                request_data: analysisResult.request_data || {}
              });
              setFilteredSuppliers(analysisResult.suppliers || []);
            }
          }
          console.log('✅ Analysis result refreshed after action');
        }
      }
    } catch (error) {
      console.error('Error refreshing analysis result:', error);
    }
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ANALYZE_REQUEST_MORE(request._id)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('More analysis response:', result);
      
      if (result.success && result.data) {
        // Handle both old and new formats
        const updatedAnalysis = result.data.updated_analysis;
        
        if (updatedAnalysis) {
          // Check if it's the new format
          if (updatedAnalysis.ai_suppliers !== undefined || updatedAnalysis.local_suppliers !== undefined) {
            // New format
            setAnalysisResult(updatedAnalysis);
            setFilteredSuppliers(updatedAnalysis.ai_suppliers || []);
          } else {
            // Old format
            setAnalysisResult({
              ai_suppliers: updatedAnalysis.suppliers || [],
              local_suppliers: [],
              request_data: updatedAnalysis.request_data || {}
            });
            setFilteredSuppliers(updatedAnalysis.suppliers || []);
          }
        }
        
        console.log('More analysis result updated:', updatedAnalysis);
        
        // Show success message
        alert(t('common.successfully_added_new_suppliers', { count: result.data.new_suppliers?.length || 0 }));
      } else {
        console.error('More analysis failed:', result);
        alert(t('common.failed_to_load_more_suppliers', { error: result.error || 'Unknown error' }));
      }
    } catch (error) {
      console.error('Error loading more suppliers:', error);
      alert(t('common.failed_to_load_more_suppliers_try_again'));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleFilterSuppliers = () => {
    // Handle both old and new formats
    let suppliersToFilter = [];
    
    if (analysisResult) {
      if (analysisResult.ai_suppliers !== undefined) {
        // New format
        suppliersToFilter = analysisResult.ai_suppliers || [];
      } else {
        // Old format
        suppliersToFilter = analysisResult.suppliers || [];
      }
    }
    
    if (!suppliersToFilter.length) return;
    
    let filtered = suppliersToFilter;
    
    // Filter by region
    if (filterCriteria.region) {
      filtered = filtered.filter((supplier: any) => 
        supplier.region?.toLowerCase().includes(filterCriteria.region.toLowerCase())
      );
    }
    
    // Filter by price (basic check)
    if (filterCriteria.price) {
      filtered = filtered.filter((supplier: any) => 
        supplier.products?.some((product: any) => 
          product.price?.toLowerCase().includes(filterCriteria.price.toLowerCase())
        )
      );
    }
    
    // Filter by delivery time
    if (filterCriteria.delivery) {
      filtered = filtered.filter((supplier: any) => 
        supplier.delivery_time?.toLowerCase().includes(filterCriteria.delivery.toLowerCase())
      );
    }
    
    // Filter by email status
    if (filterCriteria.emailStatus !== 'all') {
      filtered = filtered.filter((supplier: any) => {
        if (filterCriteria.emailStatus === 'sent') {
          return supplier.email_sent === true;
        } else if (filterCriteria.emailStatus === 'not_sent') {
          return !supplier.email_sent;
        }
        return true;
      });
    }
    
    setFilteredSuppliers(filtered);
  };

  const clearFilters = () => {
    setFilterCriteria({
      region: '',
      price: '',
      delivery: '',
      emailStatus: 'all'
    });
    
    // Handle both old and new formats
    if (analysisResult) {
      if (analysisResult.ai_suppliers !== undefined) {
        // New format
        setFilteredSuppliers(analysisResult.ai_suppliers || []);
      } else {
        // Old format
        setFilteredSuppliers(analysisResult.suppliers || []);
      }
    }
  };

  // Manual supplier functions
  const handleManualFormChange = (field: string, value: string) => {
    setManualSupplierForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addManualSupplier = async () => {
    if (!manualSupplierForm.supplier_name || !manualSupplierForm.product_name || !manualSupplierForm.price) {
      alert(t('common.please_fill_required_fields'));
      return;
    }

    setIsAddingManual(true);
    try {
      const newSupplier = {
        ...manualSupplierForm,
        id: `manual_${Date.now()}`,
        added_at: new Date().toISOString(),
        added_by: 'admin'
      };

      setManualSuppliers(prev => [...prev, newSupplier]);
      
      // Reset form
      setManualSupplierForm({
        supplier_name: '',
        supplier_email: '',
        supplier_phone: '',
        supplier_website: '',
        supplier_region: 'TR',
        product_name: '',
        product_brand: '',
        product_description: '',
        product_category: '',
        price: '',
        currency: 'TRY',
        delivery_time: '',
        minimum_order: '',
        payment_terms: '',
        notes: ''
      });

      // Save to backend
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${request._id}/manual-suppliers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ supplier: newSupplier })
      });

      if (!response.ok) {
        throw new Error('Failed to save manual supplier');
      }

      console.log('✅ Manual supplier added successfully');
    } catch (error) {
      console.error('❌ Error adding manual supplier:', error);
      alert(t('common.failed_to_add_manual_supplier'));
    } finally {
      setIsAddingManual(false);
    }
  };

  const removeManualSupplier = (supplierId: string) => {
    setManualSuppliers(prev => prev.filter(s => s.id !== supplierId));
  };

  const handleRunAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ANALYZE_REQUEST(request._id)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Analysis response:', result);
      
      if (result.success && result.data) {
        // Check if there's an error in the analysis result
        if (result.data.error) {
          console.error('Analysis error:', result.data.error);
          console.error('JSON error:', result.data.json_error);
          console.error('Content:', result.data.content);
          alert(t('common.analysis_completed_but_error'));
          return;
        }
        
        // Handle both old and new formats
        const analysisData = result.data;
        
        // Check if it's the new format
        if (analysisData.ai_suppliers !== undefined || analysisData.local_suppliers !== undefined) {
          // New format
          setAnalysisResult(analysisData);
          setFilteredSuppliers(analysisData.ai_suppliers || []);
        } else {
          // Old format
          setAnalysisResult({
            ai_suppliers: analysisData.suppliers || [],
            local_suppliers: [],
            request_data: analysisData.request_data || {}
          });
          setFilteredSuppliers(analysisData.suppliers || []);
        }
        
        console.log('Analysis result set:', analysisData);
      } else {
        console.error('Analysis failed:', result);
        alert(t('common.analysis_failed', { error: result.error || 'Unknown error' }));
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      alert(t('common.failed_to_run_analysis_try_again'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Custom specifications helper functions
  const addFilterSuggestion = (suggestion: any) => {
    setNewCustomSpecKey(suggestion.key);
    setActiveFilterType(suggestion.type);
    setNewCustomSpecValue('');
  };

  const addCustomSpecification = () => {
    if (!newCustomSpecKey.trim() || !newCustomSpecValue.trim()) return;

    const newSpec = { key: newCustomSpecKey, value: newCustomSpecValue };
    setEditableProduct(prev => ({
      ...prev,
      custom_specifications: [...(Array.isArray(prev.custom_specifications) ? prev.custom_specifications : []), newSpec]
    }));

    setNewCustomSpecKey('');
    setNewCustomSpecValue('');
    setActiveFilterType('text');
  };

  const removeCustomSpecification = (index: number) => {
    setEditableProduct(prev => ({
      ...prev,
      custom_specifications: (Array.isArray(prev.custom_specifications) ? prev.custom_specifications : []).filter((_: any, i: number) => i !== index)
    }));
  };

  const handleUpdateProduct = async () => {
    if (!editableProduct.product_name.trim()) {
      alert(t('common.product_name_required'));
      return;
    }

    setIsUpdatingProduct(true);
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_REQUEST(request._id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editableProduct)
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Product information updated successfully');
        setIsEditingProduct(false);
        // Refresh the page to show updated data
        window.location.reload();
      } else {
        alert(result.message || t('common.failed_to_update_product'));
      }
    } catch (error) {
      console.error('Error updating product information:', error);
      alert(t('common.failed_to_update_product_try_again'));
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  const handleLocalSupplierEmail = (supplier: any, index: number) => {
    console.log('🔍 handleLocalSupplierEmail called with:', { supplier, index });
    console.log('🔍 supplier.name:', supplier?.name);
    console.log('🔍 supplier.email:', supplier?.email);
    
    setLocalEmailModal({
      isOpen: true,
      supplierIndex: index,
      supplierName: supplier.name,
      supplierEmail: supplier.email
    });
  };

  const handleLocalEmailSent = () => {
    setLocalEmailModal({
      isOpen: false,
      supplierIndex: -1,
      supplierName: '',
      supplierEmail: ''
    });
    // Optionally refresh the analysis to update email status
    handleRunAnalysis();
  };

  const handleRefreshLocal = async () => {
    setIsRefreshingLocal(true);
    try {
      await handleRunAnalysis();
    } finally {
      setIsRefreshingLocal(false);
    }
  };

  const handleMarkEmailReceived = async (supplierIndex: number) => {
    if (!token) return;
    
    try {
              const response = await fetch(API_ENDPOINTS.MARK_EMAIL_RECEIVED(request._id, supplierIndex.toString()), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Update the supplier's email_received status locally
        if (analysisResult?.local_suppliers && analysisResult.local_suppliers[supplierIndex]) {
          analysisResult.local_suppliers[supplierIndex].email_received = true;
          analysisResult.local_suppliers[supplierIndex].email_received_at = new Date().toISOString();
        }
        
        if (result.phase_updated) {
          // Phase was updated, refresh the request data
          handleActionCompleted();
        }
      } else {
        console.error('Failed to mark email as received');
      }
    } catch (error) {
      console.error('Error marking email as received:', error);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    console.log('🔍 AdminRequestModal - Overlay clicked');
    // Only close if clicking directly on the overlay, not on modal content
    if (e.target === e.currentTarget) {
      console.log('🔍 AdminRequestModal - Closing modal via overlay click');
      handleClose();
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={handleOverlayClick}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h2>{t('common.request_details')}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="admin-modal-body">
          <div className="request-info">
            <div className="info-section">
              <div className="section-header">
                <h3>{t('common.product_information')}</h3>
                <button 
                  className="edit-product-btn"
                  onClick={() => setIsEditingProduct(!isEditingProduct)}
                  disabled={isUpdatingProduct}
                >
                  {isEditingProduct ? t('common.cancel_edit') : t('common.edit')}
                </button>
              </div>
              
              {isEditingProduct ? (
                <div className="product-edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="product-name">{t('components.admin_request_modal.product_name')} *</label>
                      <input
                        id="product-name"
                        type="text"
                        value={editableProduct.product_name}
                        onChange={(e) => setEditableProduct({...editableProduct, product_name: e.target.value})}
                        placeholder={t('components.admin_request_modal.enter_product_name')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="product-brand">{t('common.brand')}</label>
                      <input
                        id="product-brand"
                        type="text"
                        value={editableProduct.product_brand || ''}
                        onChange={(e) => setEditableProduct({...editableProduct, product_brand: e.target.value})}
                        placeholder={t('common.enter_product_brand')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="product-global-code">{t('common.global_code')}</label>
                      <input
                        id="product-global-code"
                        type="text"
                        value={editableProduct.product_global_code || ''}
                        onChange={(e) => setEditableProduct({...editableProduct, product_global_code: e.target.value})}
                        placeholder={t('common.enter_global_product_code')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="product-company-code">{t('common.company_code')}</label>
                      <input
                        id="product-company-code"
                        type="text"
                        value={editableProduct.product_company_code || ''}
                        onChange={(e) => setEditableProduct({...editableProduct, product_company_code: e.target.value})}
                        placeholder={t('common.enter_company_product_code')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="product-description">{t('common.description')}</label>
                    <textarea
                      id="product-description"
                      value={editableProduct.product_description || ''}
                      onChange={(e) => setEditableProduct({...editableProduct, product_description: e.target.value})}
                      placeholder={t('common.enter_product_description')}
                      rows={3}
                      className="product-textarea"
                      disabled={isUpdatingProduct}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="quantity">{t('common.quantity')}</label>
                      <input
                        id="quantity"
                        type="number"
                        value={editableProduct.quantity || 0}
                        onChange={(e) => setEditableProduct({...editableProduct, quantity: parseInt(e.target.value) || 0})}
                        placeholder={t('common.enter_quantity')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="unit">{t('common.unit')}</label>
                      <select
                        id="unit"
                        value={editableProduct.unit || 'piece'}
                        onChange={(e) => setEditableProduct({...editableProduct, unit: e.target.value})}
                        className="product-select"
                        disabled={isUpdatingProduct}
                      >
                        {UNIT_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{t(`common.${option.value}`) || option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="budget">{t('common.budget')}</label>
                      <input
                        id="budget"
                        type="number"
                        value={editableProduct.budget || 0}
                        onChange={(e) => setEditableProduct({...editableProduct, budget: parseFloat(e.target.value) || 0})}
                        placeholder={t('common.enter_budget')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="currency">{t('common.currency')}</label>
                      <select
                        id="currency"
                        value={editableProduct.currency || 'CHF'}
                        onChange={(e) => setEditableProduct({...editableProduct, currency: e.target.value})}
                        className="product-select"
                        disabled={isUpdatingProduct}
                      >
                        {CURRENCY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="delivery-time">{t('common.delivery_time')}</label>
                      <input
                        id="delivery-time"
                        type="text"
                        value={editableProduct.delivery_time || ''}
                        onChange={(e) => setEditableProduct({...editableProduct, delivery_time: e.target.value})}
                        placeholder="e.g., 2 weeks, ASAP"
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="importance">{t('common.importance')}</label>
                      <select
                        id="importance"
                        value={editableProduct.importance || 'price'}
                        onChange={(e) => setEditableProduct({...editableProduct, importance: e.target.value as 'time' | 'price' | 'quality'})}
                        className="product-select"
                        disabled={isUpdatingProduct}
                      >
                                              <option value="time">{t('common.time_critical')}</option>
                      <option value="price">{t('common.price_sensitive')}</option>
                      <option value="quality">{t('common.quality_focused')}</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address">{t('common.delivery_address')}</label>
                    <input
                      id="address"
                      type="text"
                      value={editableProduct.address || ''}
                      onChange={(e) => setEditableProduct({...editableProduct, address: e.target.value})}
                      placeholder={t('common.enter_delivery_address')}
                      className="product-input"
                      disabled={isUpdatingProduct}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="region">{t('common.region')}</label>
                    <div className="region-checkboxes">
                      {REGION_OPTIONS.map(option => (
                        <label key={option.value} className="region-checkbox">
                          <input
                            type="checkbox"
                            checked={editableProduct.region.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditableProduct(prev => ({
                                  ...prev,
                                  region: [...prev.region, option.value]
                                }));
                              } else {
                                setEditableProduct(prev => ({
                                  ...prev,
                                  region: prev.region.filter(r => r !== option.value)
                                }));
                              }
                            }}
                            disabled={isUpdatingProduct}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="additional-notes">{t('common.additional_notes')}</label>
                    <textarea
                      id="additional-notes"
                      value={editableProduct.additional_notes || ''}
                      onChange={(e) => setEditableProduct({...editableProduct, additional_notes: e.target.value})}
                      placeholder={t('common.enter_additional_notes')}
                      rows={3}
                      className="product-textarea"
                      disabled={isUpdatingProduct}
                    />
                  </div>
                  
                  {/* Additional Specifications */}
                  <div className="form-group">
                    <label htmlFor="additional-specifications">{t('common.additional_specifications')}</label>
                    <div className="filter-suggestions">
                      {FILTER_SUGGESTIONS.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => addFilterSuggestion(suggestion)}
                          className="filter-suggestion-button"
                          disabled={isUpdatingProduct}
                        >
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Specifications */}
                  <div className="form-group">
                    <label htmlFor="custom-specifications">{t('common.custom_specifications')}</label>
                    <div className="add-spec-row">
                      <input
                        type="text"
                        value={newCustomSpecKey}
                        onChange={(e) => setNewCustomSpecKey(e.target.value)}
                        placeholder={t('common.specification_name')}
                        className="product-input"
                        disabled={isUpdatingProduct}
                      />
                      {activeFilterType === 'dropdown' ? (
                        <select
                          value={newCustomSpecValue}
                          onChange={(e) => setNewCustomSpecValue(e.target.value)}
                          className="product-select"
                          disabled={isUpdatingProduct}
                        >
                          <option value="">{t('common.select_value')}</option>
                          {FILTER_SUGGESTIONS.find(s => s.key === newCustomSpecKey)?.options?.map((option, index) => (
                            <option key={index} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={newCustomSpecValue}
                          onChange={(e) => setNewCustomSpecValue(e.target.value)}
                          placeholder={FILTER_SUGGESTIONS.find(s => s.key === newCustomSpecKey)?.placeholder || t('common.enter_specification_value')}
                          className="product-input"
                          disabled={isUpdatingProduct}
                        />
                      )}
                      <button
                        type="button"
                        onClick={addCustomSpecification}
                        className="add-spec-button"
                        disabled={isUpdatingProduct || !newCustomSpecKey.trim() || !newCustomSpecValue.trim()}
                      >
                        {t('common.add')}
                      </button>
                    </div>

                    {Array.isArray(editableProduct.custom_specifications) && editableProduct.custom_specifications.length > 0 && (
                      <div className="custom-specifications">
                        {editableProduct.custom_specifications.map((spec, index) => (
                          <div key={index} className="spec-item">
                            <span className="spec-key">{spec.key}:</span>
                            <span className="spec-value">{spec.value}</span>
                            <button
                              type="button"
                              onClick={() => removeCustomSpecification(index)}
                              className="remove-spec-button"
                              disabled={isUpdatingProduct}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="product-edit-actions">
                    <button
                      className="update-product-btn"
                      onClick={handleUpdateProduct}
                      disabled={isUpdatingProduct || !editableProduct.product_name.trim()}
                    >
                      {isUpdatingProduct ? t('common.updating') : t('common.update_product')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.product_name')}:</label>
                    <span>{request.product_name}</span>
                  </div>
                  {request.product_brand && (
                    <div className="info-item">
                      <label>{t('common.brand')}:</label>
                      <span>{request.product_brand}</span>
                    </div>
                  )}
                  {request.product_global_code && (
                    <div className="info-item">
                      <label>{t('common.global_code')}:</label>
                      <span>{request.product_global_code}</span>
                    </div>
                  )}
                  {request.product_company_code && (
                    <div className="info-item">
                      <label>{t('common.company_code')}:</label>
                      <span>{request.product_company_code}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.category')}:</label>
                    <span>{request.product_category}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.description')}:</label>
                    <span>{request.product_description || t('components.admin_request_modal.no_description_provided')}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.quantity')}:</label>
                    <span>{request.quantity} {t(`components.admin_request_modal.${request.unit}`) || request.unit}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.budget')}:</label>
                    <span>{request.currency || 'CHF'} {request.budget}</span>
                  </div>
                  {request.delivery_time && (
                    <div className="info-item">
                      <label>{t('components.admin_request_modal.delivery_time')}:</label>
                      <span>{request.delivery_time}</span>
                    </div>
                  )}
                  {request.address && (
                    <div className="info-item">
                      <label>{t('common.delivery_address')}:</label>
                      <span>{request.address}</span>
                    </div>
                  )}
                  {request.importance && (
                    <div className="info-item">
                      <label>{t('components.admin_request_modal.importance')}:</label>
                      <span className={`priority-badge ${request.importance}`}>
                        {t(`components.admin_request_modal.${request.importance}`) || request.importance}
                      </span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>{t('components.admin_request_modal.urgency')}:</label>
                    <span className={`urgency-badge ${request.urgency}`}>
                      {t(`components.admin_request_modal.${request.urgency}`) || request.urgency}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* OCR Information Section - Show only if request was created from OCR */}
            {request.is_ocr_generated && (
              <div className="form-section ocr-section">
                <h3 className="section-title">
                    📄 {t('components.admin_request_modal.ocr_generated_request')}
                  <span className="ocr-badge">OCR</span>
                </h3>
                
                <div className="ocr-info-grid">
                  {/* OCR Price Information */}
                  {request.ocr_prices && Object.keys(request.ocr_prices).length > 0 && (
                    <div className="ocr-price-info">
                      <h4>💰 {t('components.admin_request_modal.price_information')}</h4>
                      <div className="price-details">
                        {request.ocr_prices.unit_price && (
                          <div className="price-item">
                            <span className="price-label">{t('common.price')}:</span>
                            <span className="price-value">
                              {request.ocr_prices.currency} {request.ocr_prices.unit_price}
                            </span>
                          </div>
                        )}
                        {request.ocr_prices.total_price && (
                          <div className="price-item">
                            <span className="price-label">{t('components.admin_request_modal.total_price')}:</span>
                            <span className="price-value">
                              {request.ocr_prices.currency} {request.ocr_prices.total_price}
                            </span>
                          </div>
                        )}
                        {request.ocr_prices.budget && (
                          <div className="price-item">
                            <span className="price-label">{t('components.admin_request_modal.budget')}:</span>
                            <span className="price-value">
                              {request.ocr_prices.currency} {request.ocr_prices.budget}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OCR Supplier Information */}
                  {request.ocr_supplier_info && Object.keys(request.ocr_supplier_info).length > 0 && (
                    <div className="ocr-supplier-info">
                      <h4>🏢 {t('common.supplier_information')}</h4>
                      <div className="supplier-details">
                        {request.ocr_supplier_info.supplier_name && (
                          <div className="supplier-item">
                            <span className="supplier-label">{t('common.name')}:</span>
                            <span className="supplier-value">{request.ocr_supplier_info.supplier_name}</span>
                          </div>
                        )}
                        {request.ocr_supplier_info.supplier_email && (
                          <div className="supplier-item">
                            <span className="supplier-label">{t('common.email')}:</span>
                            <span className="supplier-value">{request.ocr_supplier_info.supplier_email}</span>
                          </div>
                        )}
                        {request.ocr_supplier_info.supplier_phone && (
                          <div className="supplier-item">
                            <span className="supplier-label">{t('common.phone')}:</span>
                            <span className="supplier-value">{request.ocr_supplier_info.supplier_phone}</span>
                          </div>
                        )}
                        {request.ocr_supplier_info.supplier_address && (
                          <div className="supplier-item">
                            <span className="supplier-label">{t('common.address')}:</span>
                            <span className="supplier-value">{request.ocr_supplier_info.supplier_address}</span>
                          </div>
                        )}
                        {request.ocr_supplier_info.supplier_website && (
                          <div className="supplier-item">
                            <span className="supplier-label">{t('common.website')}:</span>
                            <span className="supplier-value">
                              <a href={`https://${request.ocr_supplier_info.supplier_website}`} target="_blank" rel="noopener noreferrer">
                                {request.ocr_supplier_info.supplier_website}
                              </a>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* OCR Information section ends here - no quick action buttons */}
              </div>
            )}

            {/* OCR Add to Database Button */}
            {request.is_ocr_generated && (
              <div className="ocr-actions">
                <button
                  className="add-to-db-btn"
                  onClick={() => setOcrDatabaseModal({ isOpen: true, requestData: request })}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                  {t('common.add_to_database')}
                </button>
              </div>
            )}

            <div className="info-section">
                              <h3>{t('components.admin_request_modal.requester_information')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('common.name')}:</label>
                  <span>{request.user_name || 'Unknown'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.email')}:</label>
                  <span>{request.user_email || 'Unknown'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.department')}:</label>
                  <span>{request.user_department || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {request.current_supplier && (
              <div className="info-section">
                <h3>{t('components.admin_request_modal.current_supplier_information')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('common.current_supplier')}:</label>
                    <span>{request.current_supplier}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('common.current_price')}:</label>
                    <span>${request.current_price}</span>
                  </div>
                </div>
              </div>
            )}

            {request.custom_filters && Object.keys(request.custom_filters).length > 0 && (
              <div className="info-section">
                <h3>{t('common.custom_filters')}</h3>
                <div className="custom-filters-grid">
                  {Object.entries(request.custom_filters).map(([key, value]: [string, any]) => (
                    <div key={key} className="filter-item">
                      <label>{key}:</label>
                      <span>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {request.additional_notes && (
              <div className="info-section">
                <h3>{t('common.additional_notes')}</h3>
                <p className="notes-text">{request.additional_notes}</p>
              </div>
            )}

            <div className="info-section">
                              <h3>{t('components.admin_request_modal.request_timeline')}</h3>
              <div className="info-grid">
                <div className="info-item">
                                      <label>{t('components.admin_request_modal.submitted')}:</label>
                  <span>{new Date(request.created_at).toLocaleString()}</span>
                </div>
                <div className="info-item">
                                      <label>{t('components.admin_request_modal.last_updated')}:</label>
                  <span>{new Date(request.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {analysisResult && (
              <div className="info-section">
                <h3>{t('components.admin_request_modal.supplier_analysis_results')}</h3>
                
                {/* Tab Navigation */}
                <div className="supplier-tabs">
                  <button 
                    className={`tab-button ${activeTab === 'ai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai')}
                  >
                    {t('components.admin_request_modal.ai_recommendations')} ({analysisResult.ai_suppliers?.length || 0})
                  </button>
                  <button 
                    className={`tab-button ${activeTab === 'local' ? 'active' : ''}`}
                    onClick={() => setActiveTab('local')}
                  >
                    {t('components.admin_request_modal.local_database')} ({analysisResult.local_suppliers?.length || 0})
                  </button>
                  {request.selected_approved_suppliers && request.selected_approved_suppliers.length > 0 && (
                    <button 
                      className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`}
                      onClick={() => setActiveTab('approved')}
                    >
                      {t('common.approved_suppliers')} ({request.selected_approved_suppliers.length})
                    </button>
                  )}
                  <button 
                    className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
                    onClick={() => setActiveTab('manual')}
                  >
                    {t('common.manual_add')} ({manualSuppliers.length})
                  </button>
                </div>

                <div className="analysis-results">
                  {/* Analysis Summary */}
                  <div className="analysis-summary">
                    <h4>{t('components.admin_request_modal.analysis_summary')}</h4>
                    <div className="info-grid">
                      <div className="info-item">
                                                  <label>{t('components.admin_request_modal.ai_suppliers_found')}:</label>
                        <span>{analysisResult.ai_suppliers?.length || 0}</span>
                      </div>
                      <div className="info-item">
                                                  <label>{t('components.admin_request_modal.local_suppliers_found')}:</label>
                        <span>{analysisResult.local_suppliers?.length || 0}</span>
                      </div>
                      <div className="info-item">
                                                  <label>{t('components.admin_request_modal.analysis_date')}:</label>
                        <span>{request.analyzed_at ? new Date(request.analyzed_at).toLocaleString() : new Date().toLocaleDateString()}</span>
                      </div>
                      {request.analyzed_at && (
                        <div className="info-item">
                          <label>Status:</label>
                          <span style={{color: '#374151', fontWeight: 'bold'}}>✓ {t('components.admin_request_modal.analysis_complete')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Suppliers Tab */}
                  {activeTab === 'ai' && analysisResult.ai_suppliers && analysisResult.ai_suppliers.length > 0 && (
                    <div className="suppliers-section">
                      <div className="suppliers-header">
                        <h4>{t('components.admin_request_modal.ai_recommended_suppliers')} ({analysisResult.ai_suppliers.length})</h4>
                        <div className="filter-controls">
                          <input
                            type="text"
                                                          placeholder={t('components.admin_request_modal.filter_by_region')}
                            value={filterCriteria.region}
                            onChange={(e) => setFilterCriteria({...filterCriteria, region: e.target.value})}
                            className="filter-input"
                          />
                          <input
                            type="text"
                                                          placeholder={t('components.admin_request_modal.filter_by_price')}
                            value={filterCriteria.price}
                            onChange={(e) => setFilterCriteria({...filterCriteria, price: e.target.value})}
                            className="filter-input"
                          />
                          <input
                            type="text"
                                                          placeholder={t('components.admin_request_modal.filter_by_delivery')}
                            value={filterCriteria.delivery}
                            onChange={(e) => setFilterCriteria({...filterCriteria, delivery: e.target.value})}
                            className="filter-input"
                          />
                          
                          <select
                            value={filterCriteria.emailStatus}
                            onChange={(e) => setFilterCriteria({...filterCriteria, emailStatus: e.target.value})}
                            className="filter-select"
                          >
                                                          <option value="all">{t('components.admin_request_modal.all_email_status')}</option>
                                                          <option value="sent">{t('components.admin_request_modal.email_sent')}</option>
                            <option value="not_sent">{t('common.email_not_sent')}</option>
                          </select>

                          <button onClick={handleFilterSuppliers} className="filter-btn">{t('common.filter')}</button>
                          <button onClick={clearFilters} className="clear-btn">{t('common.clear')}</button>
                        </div>
                      </div>
                      <div className="suppliers-list">
                        {analysisResult.ai_suppliers.map((supplier: any, index: number) => (
                          <div key={index} className="supplier-card">
                            <div className="supplier-header">
                              <h5>{supplier.company_name}</h5>
                              {supplier.rating && (
                                <span className="rating">★ {supplier.rating}/5</span>
                              )}
                            </div>
                            <div className="supplier-details">
                              <div className="contact-info">
                                <p><strong>{t('common.website')}:</strong> <a href={supplier.website} target="_blank" rel="noopener noreferrer">{supplier.website}</a></p>
                                <p><strong>{t('common.email')}:</strong> {supplier.email}</p>
                                {supplier.phone && <p><strong>{t('common.phone')}:</strong> {supplier.phone}</p>}
                                <p><strong>{t('common.region')}:</strong> {supplier.region}</p>
                                {supplier.delivery_time && <p><strong>{t('common.delivery_time')}:</strong> {supplier.delivery_time}</p>}
                              </div>
                              {supplier.products && supplier.products.length > 0 && (
                                <div className="products-section">
                                  <h6>{t('components.admin_request_modal.products')}:</h6>
                                  {supplier.products.map((product: any, pIndex: number) => (
                                    <div key={pIndex} className="product-item">
                                      <p><strong>{product.name}</strong> - {product.price}</p>
                                      <p>{product.description}</p>
                                      {product.link && (
                                        <a href={product.link} target="_blank" rel="noopener noreferrer">{t('components.admin_request_modal.view_product')}</a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="supplier-actions">
                                <SupplierActionButton 
                                  supplier={supplier} 
                                  requestId={request._id} 
                                  supplierIndex={index}
                                  onActionCompleted={handleActionCompleted}
                                  requestData={request}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Local Suppliers Tab */}
                  {activeTab === 'local' && (
                    <div className="suppliers-section">
                      <div className="suppliers-header">
                        <div className="header-left">
                          <h4>{t('common.local_database_suppliers')} {analysisResult?.local_suppliers ? `(${analysisResult.local_suppliers.length})` : ''}</h4>
                        </div>
                        <button 
                          className="refresh-local-btn"
                          onClick={handleRefreshLocal}
                          disabled={isRefreshingLocal}
                          title={t('common.refresh_local_database_search')}
                        >
                          {isRefreshingLocal ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                              <path d="M1 4v6h6"></path>
                              <path d="M23 20v-6h-6"></path>
                              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 4v6h6"></path>
                              <path d="M23 20v-6h-6"></path>
                              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                            </svg>
                          )}
                        </button>
                      </div>
                      
                      {analysisResult?.local_suppliers && analysisResult.local_suppliers.length > 0 ? (
                        <div className="suppliers-list">
                          {analysisResult.local_suppliers.map((item: any, index: number) => {
                            const supplier = item.supplier;
                            const matchingProducts = item.matching_products;
                            
                            return (
                              <div key={index} className="supplier-card local-supplier">
                                <div className="supplier-header">
                                  <h5>{supplier.name}</h5>
                                  <span className="local-badge">Local DB</span>
                                </div>
                                <div className="supplier-details">
                                  <div className="contact-info">
                                    <div className="supplier-basic-info">
                                      <h6>{t('common.supplier_basic_info')}</h6>
                                      <p><strong>{t('common.name')}:</strong> {supplier.name}</p>
                                      <p><strong>{t('common.email')}:</strong> {supplier.email}</p>
                                      {supplier.phone && <p><strong>{t('common.phone')}:</strong> {supplier.phone}</p>}
                                      {supplier.website && (
                                        <p><strong>{t('common.website')}:</strong> <a href={supplier.website} target="_blank" rel="noopener noreferrer">{supplier.website}</a></p>
                                      )}
                                    </div>
                                    
                                    <div className="supplier-location-info">
                                      <h6>{t('common.location_category')}</h6>
                                      <p><strong>{t('common.region')}:</strong> {supplier.region}</p>
                                      <p><strong>{t('common.category')}:</strong> {supplier.category}</p>
                                      {supplier.country && <p><strong>{t('common.country')}:</strong> {supplier.country}</p>}
                                      {supplier.address && <p><strong>{t('common.address')}:</strong> {supplier.address}</p>}
                                    </div>
                                    
                                    {supplier.certifications && supplier.certifications.length > 0 && (
                                      <div className="supplier-certifications">
                                        <h6>{t('common.certifications')}</h6>
                                        <p><strong>{t('common.certifications')}:</strong> {supplier.certifications.join(', ')}</p>
                                      </div>
                                    )}
                                    
                                    {supplier.supplier_type && (
                                      <div className="supplier-type">
                                        <h6>{t('common.type')}</h6>
                                        <p><strong>{t('common.supplier_type')}:</strong> {supplier.supplier_type}</p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {matchingProducts && matchingProducts.length > 0 && (
                                    <div className="products-section">
                                      <h6>{t('common.matching_products', { count: matchingProducts.length })}</h6>
                                      {matchingProducts.map((product: any, pIndex: number) => (
                                        <div key={pIndex} className="product-item">
                                          <div className="product-header">
                                            <span className="product-title"><strong>{product.product_name}</strong></span>
                                            {product.specific_id && (
                                              <span className="product-id">ID: {product.specific_id}</span>
                                            )}
                                          </div>
                                          <div className="product-details">
                                              {product.product_brand && <p><strong>{t('common.brand')}:</strong> {product.product_brand}</p>}
                                              {product.price && <p><strong>{t('common.current_price')}:</strong> {product.price} {product.currency || 'CHF'}</p>}
                                              {product.delivery_time && <p><strong>{t('common.delivery_time')}:</strong> {product.delivery_time}</p>}
                                              {product.minimum_order && <p><strong>{t('common.minimum_order')}:</strong> {product.minimum_order}</p>}
                                              {product.payment_terms && <p><strong>{t('common.payment_terms')}:</strong> {product.payment_terms}</p>}
                                              {product.product_description && <p><strong>{t('common.description')}:</strong> {product.product_description}</p>}
                                              {product.product_global_code && <p><strong>{t('common.global_code')}:</strong> {product.product_global_code}</p>}
                                              {product.product_company_code && <p><strong>{t('common.company_code')}:</strong> {product.product_company_code}</p>}
                                            
                                            {/* OCR Historical Data */}
                                            {product.ocr_unit_price && (
                                              <div className="ocr-historical-data">
                                                <p><strong>{t('common.ocr_price')}:</strong> {product.ocr_unit_price} {product.ocr_currency || product.currency}</p>
                                                {product.ocr_supplier_name && <p><strong>{t('common.ocr_supplier')}:</strong> {product.ocr_supplier_name}</p>}
                                                {product.ocr_purchase_date && (
                                                  <p><strong>{t('common.ocr_date')}:</strong> {new Date(product.ocr_purchase_date).toLocaleDateString()}</p>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  <div className="supplier-actions">
                                    <button 
                                      className="send-email-btn"
                                      onClick={() => handleLocalSupplierEmail(supplier, index)}
                                      disabled={!supplier || !supplier.email}
                                    >
                                      {t('common.send_email')}
                                    </button>
                                    <button 
                                      className="view-details-btn"
                                      onClick={() => {
                                        console.log('View supplier details:', supplier);
                                      }}
                                    >
                                      {t('common.view_details')}
                                    </button>
                                    
                                    {/* Email Received Checkbox for Local Suppliers */}
                                    <div className="email-received-section">
                                      <label className="email-received-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={supplier.email_received || false}
                                          onChange={() => handleMarkEmailReceived(index)}
                                          disabled={supplier.email_received}
                                        />
                                        <span className="checkmark"></span>
                                        {t('common.email_received')}
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="no-results">
                                                <p>{t('common.no_matching_suppliers')}</p>
                      <p>{t('common.click_refresh_or_add')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Approved Suppliers Tab */}
                  {activeTab === 'approved' && request.selected_approved_suppliers && request.selected_approved_suppliers.length > 0 && (
                    <div className="suppliers-section">
                      <div className="suppliers-header">
                        <h4>{t('common.approved_suppliers')} ({request.selected_approved_suppliers.length})</h4>
                      </div>
                      <div className="suppliers-list">
                        {request.selected_approved_suppliers.map((supplierName: string, index: number) => {
                          // Check if email was sent for this approved supplier
                          const emailSent = request.approved_supplier_emails_sent && 
                            request.approved_supplier_emails_sent[supplierName];
                          
                          return (
                            <div key={index} className="supplier-card approved-supplier">
                              <div className="supplier-header">
                                <h5>{supplierName}</h5>
                                <span className="approved-badge">Approved</span>
                              </div>
                              <div className="supplier-details">
                                <div className="contact-info">
                                  <p><strong>{t('common.supplier_name')}:</strong> {supplierName}</p>
                                  <p><strong>{t('common.status')}:</strong> {t('common.approved')}</p>
                                </div>
                              </div>
                              <div className="supplier-actions">
                                <SupplierActionButton 
                                  supplier={{ 
                                    name: supplierName, 
                                    company_name: supplierName,
                                    email_sent: emailSent,
                                    email_sent_at: emailSent ? request.approved_supplier_emails_sent?.[supplierName] : null
                                  }} 
                                  requestId={request._id} 
                                  supplierIndex={index}
                                  onActionCompleted={() => {
                                    // Refresh the request data after action completion
                                    if (request) {
                                      // You might want to add a callback to refresh the request data
                                      console.log('Action completed for approved supplier:', supplierName);
                                    }
                                  }}
                                  requestData={request}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Manual Add Tab */}
                  {activeTab === 'manual' && (
                    <div className="suppliers-section">
                      <div className="suppliers-header">
                        <h4>{t('common.manual_add_suppliers')}</h4>
                        <p>{t('common.manual_add_description')}</p>
                      </div>
                      
                      {/* Manual Add Form */}
                      <div className="manual-add-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('common.supplier_name')} *</label>
                            <input
                              type="text"
                              value={manualSupplierForm.supplier_name}
                              onChange={(e) => handleManualFormChange('supplier_name', e.target.value)}
                              placeholder={t('common.enter_supplier_name')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('common.supplier_email')}</label>
                            <input
                              type="email"
                              value={manualSupplierForm.supplier_email}
                              onChange={(e) => handleManualFormChange('supplier_email', e.target.value)}
                              placeholder={t('common.enter_supplier_email')}
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('common.supplier_phone')}</label>
                            <input
                              type="text"
                              value={manualSupplierForm.supplier_phone}
                              onChange={(e) => handleManualFormChange('supplier_phone', e.target.value)}
                              placeholder={t('common.enter_supplier_phone')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('common.supplier_website')}</label>
                            <input
                              type="url"
                              value={manualSupplierForm.supplier_website}
                              onChange={(e) => handleManualFormChange('supplier_website', e.target.value)}
                              placeholder={t('common.enter_supplier_website')}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('common.product_name')} *</label>
                            <input
                              type="text"
                              value={manualSupplierForm.product_name}
                              onChange={(e) => handleManualFormChange('product_name', e.target.value)}
                              placeholder={t('common.enter_product_name')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('common.product_brand')}</label>
                            <input
                              type="text"
                              value={manualSupplierForm.product_brand}
                              onChange={(e) => handleManualFormChange('product_brand', e.target.value)}
                              placeholder={t('common.enter_product_brand')}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('common.price')} *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={manualSupplierForm.price}
                              onChange={(e) => handleManualFormChange('price', e.target.value)}
                              placeholder={t('common.enter_price')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('common.currency')}</label>
                            <select
                              value={manualSupplierForm.currency}
                              onChange={(e) => handleManualFormChange('currency', e.target.value)}
                            >
                              <option value="TRY">TRY</option>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="CHF">CHF</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('common.delivery_time')}</label>
                            <input
                              type="text"
                              value={manualSupplierForm.delivery_time}
                              onChange={(e) => handleManualFormChange('delivery_time', e.target.value)}
                              placeholder={t('common.delivery_time_placeholder')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('common.supplier_region')}</label>
                            <select
                              value={manualSupplierForm.supplier_region}
                              onChange={(e) => handleManualFormChange('supplier_region', e.target.value)}
                            >
                              <option value="TR">Turkey</option>
                              <option value="EU">Europe</option>
                              <option value="US">United States</option>
                              <option value="AS">Asia</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group full-width">
                          <label>{t('common.product_description')}</label>
                          <textarea
                            value={manualSupplierForm.product_description}
                            onChange={(e) => handleManualFormChange('product_description', e.target.value)}
                            placeholder={t('common.enter_product_description')}
                            rows={3}
                          />
                        </div>

                        <div className="form-group full-width">
                          <label>{t('common.notes')}</label>
                          <textarea
                            value={manualSupplierForm.notes}
                            onChange={(e) => handleManualFormChange('notes', e.target.value)}
                            placeholder={t('common.enter_additional_notes')}
                            rows={2}
                          />
                        </div>

                        <div className="form-actions">
                          <button
                            onClick={addManualSupplier}
                            disabled={isAddingManual}
                            className="add-manual-btn"
                          >
                            {isAddingManual ? t('common.adding') : t('common.add_manual_supplier')}
                          </button>
                        </div>
                      </div>

                      {/* Manual Suppliers List */}
                      {manualSuppliers.length > 0 && (
                        <div className="manual-suppliers-list">
                          <h5>{t('common.added_manual_suppliers')} ({manualSuppliers.length})</h5>
                          <div className="suppliers-list">
                            {manualSuppliers.map((supplier, index) => (
                              <div key={supplier.id} className="supplier-card manual-supplier">
                                <div className="supplier-header">
                                  <h5>{supplier.supplier_name}</h5>
                                  <span className="manual-badge">Manual</span>
                                  <button
                                    onClick={() => removeManualSupplier(supplier.id)}
                                    className="remove-btn"
                                    title={t('common.remove_supplier')}
                                  >
                                    ×
                                  </button>
                                </div>
                                <div className="supplier-details">
                                  <div className="contact-info">
                                    <p><strong>{t('common.product')}:</strong> {supplier.product_name}</p>
                                    <p><strong>{t('common.price')}:</strong> {supplier.price} {supplier.currency}</p>
                                    {supplier.supplier_email && (
                                      <p><strong>{t('common.email')}:</strong> {supplier.supplier_email}</p>
                                    )}
                                    {supplier.supplier_phone && (
                                      <p><strong>{t('common.phone')}:</strong> {supplier.supplier_phone}</p>
                                    )}
                                    {supplier.delivery_time && (
                                      <p><strong>{t('common.delivery_time')}:</strong> {supplier.delivery_time}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No Results */}
                  {activeTab === 'ai' && (!analysisResult.ai_suppliers || analysisResult.ai_suppliers.length === 0) && (
                    <div className="no-results">
                      <p>{t('common.no_ai_recommendations')}</p>
                      <p>{t('common.please_run_analysis')}</p>
                    </div>
                  )}

                  {activeTab === 'local' && (!analysisResult.local_suppliers || analysisResult.local_suppliers.length === 0) && (
                    <div className="no-results">
                      <p>{t('common.no_matching_suppliers')}</p>
                      <p>{t('common.please_run_analysis_or_add')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="admin-actions">
            <div className="action-buttons">
              {!analysisResult && (
                <button
                  className="analyze-btn"
                  onClick={handleRunAnalysis}
                  disabled={isAnalyzing}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  {isAnalyzing ? t('common.analyzing') : t('common.run_analysis')}
                </button>
              )}
              
              {analysisResult && (
                <button
                  className="more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  {isLoadingMore ? t('components.admin_request_modal.loading_more') : t('components.admin_request_modal.load_more_suppliers')}
                </button>
              )}
              
              <button
                className="approve-btn"
                onClick={handleOpenApproveModal}
                disabled={isSubmitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
                                    {isSubmitting ? t('components.admin_request_modal.approve') + '...' : t('components.admin_request_modal.approve_request')}
              </button>
              
              <RevisionButton
                requestId={request._id}
                onRevisionRequested={handleRevisionRequested}
              />
              
              <button
                className="purchase-history-btn"
                onClick={handleOpenPurchaseHistory}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                {t('components.admin_request_modal.purchase_history')}
              </button>
              
              <button
                className="reject-btn"
                onClick={() => {
                    const reason = prompt(t('common.please_provide_rejection_reason'));
                  if (reason) {
                    setRejectionReason(reason);
                    onReject(request._id, reason);
                  }
                }}
                disabled={isSubmitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                                    {isSubmitting ? t('components.admin_request_modal.reject') + '...' : t('components.admin_request_modal.reject_request')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {localEmailModal.isOpen && (
        <EmailModal
          isOpen={localEmailModal.isOpen}
          onClose={() => setLocalEmailModal({...localEmailModal, isOpen: false})}
          requestId={request._id}
          supplierIndex={localEmailModal.supplierIndex}
          supplierName={localEmailModal.supplierName}
          supplierEmail={localEmailModal.supplierEmail}
          onEmailSent={handleLocalEmailSent}
          requestData={request}
        />
      )}

      {ocrSupplierModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content ocr-modal">
            <div className="modal-header">
              <h2>{t('common.add_supplier_from_ocr')}</h2>
              <button 
                className="close-button"
                onClick={() => setOcrSupplierModal({ isOpen: false, supplierData: null })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('common.this_will_add_supplier')}</p>
              <div className="supplier-preview">
                  <h3>{t('common.supplier_information')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                      <label>{t('common.name')}:</label>
                    <span>{ocrSupplierModal.supplierData?.supplier_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                      <label>{t('common.email')}:</label>
                    <span>{ocrSupplierModal.supplierData?.supplier_email || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                      <label>{t('common.phone')}:</label>
                    <span>{ocrSupplierModal.supplierData?.supplier_phone || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setOcrSupplierModal({ isOpen: false, supplierData: null })}
                >
                  {t('common.cancel')}
                </button>
                <button 
                  className="confirm-btn"
                  onClick={() => {
                    // TODO: Implement supplier addition with OCR data
                    console.log('Adding supplier with OCR data:', ocrSupplierModal.supplierData);
                    setOcrSupplierModal({ isOpen: false, supplierData: null });
                  }}
                >
                  {t('common.add_supplier')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {ocrProductModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content ocr-modal">
            <div className="modal-header">
              <h2>{t('common.add_product_from_ocr')}</h2>
              <button 
                className="close-button"
                onClick={() => setOcrProductModal({ isOpen: false, productData: null })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('common.this_will_add_product')}</p>
              <div className="product-preview">
                  <h3>{t('common.product_information')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                      <label>{t('common.name')}:</label>
                    <span>{request.product_name}</span>
                  </div>
                  <div className="info-item">
                      <label>{t('common.brand')}:</label>
                    <span>{request.product_brand || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                      <label>{t('common.price')}:</label>
                    <span>{request.ocr_prices?.unit_price || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setOcrProductModal({ isOpen: false, productData: null })}
                >
                  {t('common.cancel')}
                </button>
                <button 
                  className="confirm-btn"
                  onClick={() => {
                    // TODO: Implement product addition with OCR data
                    console.log('Adding product with OCR data:', ocrProductModal.productData);
                    setOcrProductModal({ isOpen: false, productData: null });
                  }}
                >
                  {t('common.add_product')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PurchaseHistoryModal
        isOpen={isPurchaseHistoryOpen}
        onClose={handleClosePurchaseHistory}
      />

      {ocrDatabaseModal.isOpen && (
        <SupplierDatabaseModal
          isOpen={ocrDatabaseModal.isOpen}
          onClose={() => setOcrDatabaseModal({ isOpen: false, requestData: null })}
          requestId={request._id}
          supplierIndex={0} // OCR data için özel index
          initialSupplierData={{
            supplier_name: request.ocr_supplier_info?.supplier_name || '',
            supplier_email: request.ocr_supplier_info?.supplier_email || '',
            supplier_phone: request.ocr_supplier_info?.supplier_phone || '',
            supplier_address: request.ocr_supplier_info?.supplier_address || '',
            supplier_website: request.ocr_supplier_info?.supplier_website || '',
            region: request.region?.[0] || 'TR', // OCR'dan gelen region bilgisini kullan
            products: [{
              product_name: request.product_name,
              product_brand: request.product_brand || '',
              product_description: request.product_description || '',
              product_category: request.product_category || '',
              unit_price: request.ocr_prices?.unit_price || 0,
              total_price: request.ocr_prices?.total_price || 0,
              currency: request.ocr_prices?.currency || 'TL', // Default TL yap
              budget: request.ocr_prices?.budget || 0,
              quantity: request.quantity || 1,
              unit: request.unit || 'piece',
              delivery_time: request.delivery_time || '',
              payment_terms: '',
              notes: `OCR Generated - ${request.ocr_supplier_info?.supplier_name || 'Unknown Supplier'}`
            }]
          }}
          onSupplierAdded={() => {
            setOcrDatabaseModal({ isOpen: false, requestData: null });
            // Refresh the page or update the request status
            window.location.reload();
          }}
        />
      )}

      {/* Approve Request Modal */}
      {approveModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content approve-modal">
            <div className="modal-header">
              <h3>{t('components.admin_request_modal.approve_request')} - {t('components.admin_request_modal.select_suppliers')}</h3>
              <button className="close-btn" onClick={handleCloseApproveModal}>×</button>
            </div>
            
            <div className="modal-body">
              {approveModal.isLoading ? (
                <div className="loading-state">
                  <p>{t('common.loading_local_suppliers')}</p>
                </div>
              ) : (
                <>
                  <div className="request-info">
                    <h4>{t('common.request_information')}</h4>
                    <p><strong>{t('common.product')}:</strong> {request?.product_name}</p>
                    <p><strong>{t('common.brand')}:</strong> {request?.product_brand || 'N/A'}</p>
                    <p><strong>{t('common.category')}:</strong> {request?.category || 'N/A'}</p>
                  </div>
                  
                  <div className="suppliers-section">
                    <h4>{t('common.local_supplier_matches')}</h4>
                    <p className="help-text">{t('common.help_text')}</p>
                    
                    {approveModal.localSuppliers.length === 0 ? (
                      <div className="no-results">
                        <p>{t('common.no_local_suppliers')}</p>
                      </div>
                    ) : (
                      <div className="suppliers-grid">
                        {approveModal.localSuppliers.map((supplier, index) => {
                          const isSelected = approveModal.selectedSuppliers.some(s => 
                            s.supplier_id === supplier.supplier_id && s.product_id === supplier.product_id
                          );
                          
                          return (
                            <div key={`${supplier.supplier_id}-${supplier.product_id}`} className="supplier-card">
                              <div className="supplier-header">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => handleSupplierSelection(supplier, e.target.checked)}
                                  className="supplier-checkbox"
                                />
                                <div className="supplier-info">
                                  <h5>{supplier.supplier_name}</h5>
                                  <p className="supplier-email">{supplier.supplier_email}</p>
                                  <p className="supplier-region">{supplier.supplier_region}</p>
                                </div>
                                <div className="match-score">
                                  <span className="score-badge">{supplier.match_score}%</span>
                                </div>
                              </div>
                              
                              <div className="product-info">
                                <h6>{t('common.product')}: {supplier.product_name}</h6>
                                <p className="product-brand">{t('common.brand')}: {supplier.product_brand || 'N/A'}</p>
                                <p className="product-price">
                                  {t('common.price')}: {supplier.unit_price} {supplier.currency}
                                </p>
                                {supplier.delivery_time && (
                                  <p className="product-delivery">{t('common.delivery_time')}: {supplier.delivery_time}</p>
                                )}
                                {supplier.minimum_order && (
                                  <p className="product-min-order">{t('common.minimum_order')}: {supplier.minimum_order}</p>
                                )}
                                {supplier.payment_terms && (
                                  <p className="product-payment">{t('common.payment_terms')}: {supplier.payment_terms}</p>
                                )}
                                {supplier.product_description && (
                                  <p className="product-description">
                                    {supplier.product_description}
                                  </p>
                                )}
                                
                                {/* OCR Historical Data */}
                                {supplier.ocr_unit_price && (
                                  <div className="ocr-historical-data">
                                    <h6>{t('common.ocr_historical_data')}:</h6>
                                    <p className="ocr-price">
                                      {t('common.ocr_price')}: {supplier.ocr_unit_price} {supplier.ocr_currency || supplier.currency}
                                    </p>
                                    {supplier.ocr_supplier_name && (
                                      <p className="ocr-supplier">{t('common.ocr_supplier')}: {supplier.ocr_supplier_name}</p>
                                    )}
                                    {supplier.ocr_purchase_date && (
                                      <p className="ocr-date">
                                        {t('common.ocr_date')}: {new Date(supplier.ocr_purchase_date).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div className="selection-summary">
                    <p>
                      <strong>{t('common.selected')}:</strong> {t('common.selected_suppliers', { count: approveModal.selectedSuppliers.length })}
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCloseApproveModal}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="approve-btn"
                onClick={handleApproveWithSuppliers}
                disabled={approveModal.selectedSuppliers.length === 0 || approveModal.isLoading}
              >
                {t('common.approve_with_suppliers')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal for closing */}
      <ConfirmationModal
        isOpen={showCloseConfirmation}
        onClose={() => setShowCloseConfirmation(false)}
        onConfirm={handleConfirmClose}
        title={t('common.close_modal')}
        message={t('common.are_you_sure_close')}
        confirmText={t('common.close')}
        cancelText={t('common.cancel')}
        type="info"
      />

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={editProductModal.isOpen}
        onClose={() => setEditProductModal({ isOpen: false, selectedSuppliers: [] })}
        selectedSuppliers={editProductModal.selectedSuppliers}
        onSaveChanges={handleSaveChanges}
        onContinueToPreview={handleContinueToPreview}
      />

      {/* Preview Modal */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, updatedSuppliers: [] })}
        updatedSuppliers={previewModal.updatedSuppliers}
        onApproveRequest={() => handleApproveRequest(previewModal.updatedSuppliers)}
        onBackToEdit={handleBackToEdit}
      />
    </div>
  );
};

export default AdminRequestModal; 